import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Project = {
  name: string;
  description: string;
  label: string;
  url: string;
  displayUrl: string;
  preview: string;
};

const projects: Project[] = [
  {
    name: "Veltrix",
    description: "AI-powered workflow automation SaaS",
    label: "SaaS",
    url: "https://stellar-veltrix.vercel.app",
    displayUrl: "veltrix.dyna-agency.com",
    preview: "linear-gradient(135deg, #3b0764 0%, #0A0A0F 60%, #1e1b4b 100%)",
  },
  {
    name: "Mosswork Studio",
    description: "Brand identity & digital design agency",
    label: "Agency",
    url: "https://stellar-mosswork.vercel.app",
    displayUrl: "mossworkstudio.dyna-agency.com",
    preview: "linear-gradient(135deg, #F7F3EE 0%, #E8DDD0 50%, #C8A96E 100%)",
  },
  {
    name: "Hearthline Co.",
    description: "Artisan home goods e-commerce",
    label: "E-Commerce",
    url: "https://stellar-hearthline.vercel.app",
    displayUrl: "hearthlineco.dyna-agency.com",
    preview: "linear-gradient(135deg, #FAFAF7 0%, #E7E5E4 50%, #78716C 100%)",
  },
  {
    name: "Harborview Properties",
    description: "Luxury coastal real estate brokerage",
    label: "Real Estate",
    url: "https://stellar-harborview.vercel.app",
    displayUrl: "harborviewproperties.dyna-agency.com",
    preview: "linear-gradient(135deg, #0D1B2A 0%, #1B2E42 50%, #B8945A 100%)",
  },
  {
    name: "Thornfield Estate",
    description: "Intimate wedding venue & event space",
    label: "Landing Page",
    url: "https://stellar-thornfield.vercel.app",
    displayUrl: "thornfieldestate.dyna-agency.com",
    preview: "linear-gradient(135deg, #FAF7F2 0%, #F0EAE0 40%, #E8C4B8 80%, #C4A052 100%)",
  },
  {
    name: "Groundwork Coffee",
    description: "Specialty coffee shop & online ordering",
    label: "Landing Page",
    url: "https://stellar-groundwork.vercel.app",
    displayUrl: "groundworkcoffee.dyna-agency.com",
    preview: "linear-gradient(135deg, #1A0F0A 0%, #2D1A0F 50%, #D4824A 100%)",
  },
  {
    name: "ClearFlow Plumbing",
    description: "Full-service plumbing contractor",
    label: "Landing Page",
    url: "https://stellar-clearflow.vercel.app",
    displayUrl: "clearflowplumbing.dyna-agency.com",
    preview: "linear-gradient(135deg, #0055A4 0%, #1C2B35 50%, #F56E0F 100%)",
  },
  {
    name: "Volt & Circuit Electric",
    description: "Industrial electrical contractor",
    label: "Landing Page",
    url: "https://stellar-volt.vercel.app",
    displayUrl: "voltelectric.dyna-agency.com",
    preview: "linear-gradient(135deg, #0F1117 0%, #1A1D27 50%, #FFD600 100%)",
  },
  {
    name: "Launchpad",
    description: "Startup accelerator & founder application",
    label: "SaaS",
    url: "https://stellar-launchpad.vercel.app",
    displayUrl: "launchpadvc.dyna-agency.com",
    preview: "linear-gradient(135deg, #08090C 0%, #0F1117 40%, #6C47FF 80%, #00D4FF 100%)",
  },
  {
    name: "Meridian",
    description: "Config-driven white-label SaaS template",
    label: "SaaS",
    url: "https://stellar-whitelabel.vercel.app",
    displayUrl: "meridian.dyna-agency.com",
    preview: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 40%, #2563EB 100%)",
  },
];

// 3x clone so -33.333% lands on an exact repeat — no jump at the loop point
const track = [...projects, ...projects, ...projects];

function LivePreview({ src, preview, name }: { src: string; preview: string; name: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.25);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / 1280);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ position: "relative", height: 180, background: preview, overflow: "hidden", flexShrink: 0 }}>
      <iframe
        src={src}
        title={`${name} portfolio site preview`}
        loading="lazy"
        sandbox="allow-scripts allow-same-origin"
        style={{
          position: "absolute", top: 0, left: 0,
          width: 1280, height: 720,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          border: "none", pointerEvents: "none",
        }}
      />
    </div>
  );
}

/** Static skeleton matching the section's outer dimensions so the layout
 *  doesn't shift when the live carousel mounts client-side. */
function PortfolioSectionPlaceholder() {
  const { t } = useTranslation();
  return (
    <section
      id="work"
      className="dyna-section--alt"
      style={{ padding: "var(--s-lg) 0", overflow: "hidden", position: "relative" }}
      aria-hidden="true"
    >
      <div style={{ textAlign: "center", maxWidth: 680, margin: "0 auto var(--s-md)", padding: "0 var(--site-px)" }}>
        <h2 style={{
          fontFamily: "Coolvetica, system-ui, sans-serif",
          fontSize: "clamp(32px, 4vw, 48px)", textTransform: "uppercase",
          fontWeight: 700, letterSpacing: "0.05em", lineHeight: 1.1,
          margin: "0 0 12px", color: "var(--dyna-fg)",
        }}>
          {t("dyna.portfolio.heading")} {t("dyna.portfolio.heading_em")}
        </h2>
      </div>
      {/* Empty grid reservation matching the carousel's height so LCP/CLS stays stable. */}
      <div style={{ height: 240 }} />
    </section>
  );
}

function PortfolioSectionClient() {
  const { t } = useTranslation();
  return (
    <section id="work" className="dyna-section--alt" style={{ padding: "var(--s-lg) 0", overflow: "hidden", position: "relative" }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 21 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        style={{ textAlign: "center", maxWidth: 680, margin: "0 auto var(--s-md)", padding: "0 var(--site-px)" }}
      >
        <h2 style={{
          fontFamily: "Coolvetica, system-ui, sans-serif",
          fontSize: "clamp(32px, 4vw, 48px)", textTransform: "uppercase",
          fontWeight: 700, letterSpacing: "0.05em", lineHeight: 1.1,
          margin: "0 0 12px", color: "var(--dyna-fg)",
        }}>
          {t("dyna.portfolio.heading")} {t("dyna.portfolio.heading_em")}
        </h2>
      </motion.div>

      {/* Scrolling track */}
      <div
        style={{
          position: "relative",
          width: "100%",
          overflow: "hidden",
          maskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
          WebkitMaskImage: "linear-gradient(to right, transparent, black 5%, black 95%, transparent)",
        }}
      >
        <div
          className="portfolio-track"
          style={{ gap: 20, willChange: "transform" }}
          onMouseEnter={e => (e.currentTarget.style.animationPlayState = "paused")}
          onMouseLeave={e => (e.currentTarget.style.animationPlayState = "running")}
        >
          {track.map((p, i) => (
            <article
              key={i}
              style={{
                width: 340,
                flexShrink: 0,
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid var(--dyna-border)",
                background: "var(--char-900)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.35)",
                transition: "border-color 300ms, box-shadow 300ms, transform 300ms",
                cursor: "default",
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.borderColor = "rgba(135,115,180,0.35)";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 16px 48px rgba(135,115,180,0.22), 0 4px 16px rgba(0,0,0,0.4)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(-6px)";
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.borderColor = "var(--dyna-border)";
                (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 20px rgba(0,0,0,0.35)";
                (e.currentTarget as HTMLElement).style.transform = "translateY(0)";
              }}
            >
              {/* Browser chrome */}
              <div style={{
                padding: "10px 14px", borderBottom: "1px solid var(--dyna-border)",
                display: "flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.2)",
              }}>
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "rgba(255,80,80,0.5)", flexShrink: 0 }} />
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "rgba(255,200,0,0.4)", flexShrink: 0 }} />
                <span style={{ width: 9, height: 9, borderRadius: "50%", background: "rgba(80,220,80,0.5)", flexShrink: 0 }} />
                <div style={{
                  flex: 1, marginLeft: 6, height: 20, borderRadius: 5,
                  background: "rgba(253,248,240,0.06)", padding: "0 8px",
                  display: "flex", alignItems: "center",
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                  color: "var(--char-500)", letterSpacing: "0.04em", overflow: "hidden", whiteSpace: "nowrap",
                }}>
                  {p.displayUrl}
                </div>
                <span style={{
                  fontSize: 8, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase",
                  color: "#4ade80", background: "rgba(74,222,128,0.1)",
                  padding: "2px 7px", borderRadius: 999, border: "1px solid rgba(74,222,128,0.2)", flexShrink: 0,
                }}>
                  Live
                </span>
              </div>

              {/* iframe preview */}
              <LivePreview src={p.url} preview={p.preview} name={p.name} />

              {/* Footer */}
              <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <span style={{
                    display: "block", fontFamily: "'JetBrains Mono', monospace",
                    fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase",
                    color: "var(--lavender-500)", marginBottom: 3,
                  }}>
                    {p.label}
                  </span>
                  <h3 style={{
                    fontFamily: "Coolvetica, system-ui, sans-serif", fontSize: 16,
                    fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase",
                    color: "var(--dyna-fg)", margin: "0 0 3px",
                  }}>
                    {p.name}
                  </h3>
                  <p style={{ fontSize: 12, color: "var(--dyna-fg-muted)", lineHeight: 1.45, margin: 0 }}>
                    {p.description}
                  </p>
                </div>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 4,
                    fontSize: 11, fontWeight: 600, color: "var(--lavender-400)",
                    border: "1px solid rgba(135,115,180,0.25)", borderRadius: 999,
                    padding: "5px 12px", textDecoration: "none",
                    transition: "all 200ms", whiteSpace: "nowrap",
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.background = "var(--lavender-600)";
                    (e.currentTarget as HTMLElement).style.color = "#fff";
                    (e.currentTarget as HTMLElement).style.borderColor = "var(--lavender-600)";
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.background = "transparent";
                    (e.currentTarget as HTMLElement).style.color = "var(--lavender-400)";
                    (e.currentTarget as HTMLElement).style.borderColor = "rgba(135,115,180,0.25)";
                  }}
                >
                  Visit <ExternalLink size={10} />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .portfolio-track {
          display: flex;
          width: max-content;
          animation: marquee-ltr 30s linear infinite;
        }
        [dir="rtl"] .portfolio-track {
          animation: marquee-rtl 30s linear infinite;
        }
        @keyframes marquee-ltr {
          from { transform: translateX(0); }
          to   { transform: translateX(-33.333%); }
        }
        @keyframes marquee-rtl {
          from { transform: translateX(0); }
          to   { transform: translateX(33.333%); }
        }
      `,
        }}
      />
    </section>
  );
}

/** Default export — renders a static placeholder during SSR + first paint,
 *  swaps to the live carousel after hydration. The iframes have non-trivial
 *  client behavior (ResizeObserver, marquee animation) and were the most
 *  likely source of hydration mismatch before this guard. */
export default memo(function PortfolioSection() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <PortfolioSectionPlaceholder />;
  return <PortfolioSectionClient />;
});
